import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import InviteYourEmployeesModal from 'adminDashboard/home/common/inviteYourEmployeesModal'

import { PerksRenewalTaskIds, RenewalPlanPeriodResponse, TaskStatus, getRenewalPlanPeriod, notifyEmployees } from 'core'
import { PlanPeriodDto, getActivePlanPeriod } from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'

import { Skeleton } from 'common/components'
import { formatYear, parseZoneFormat } from 'common/dateFormatters'
import { useBreakPoints, useQueryRequestV2, useRenewalTasks, useSharedState } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import Icon from 'assets/images/perks-dashboard-home-setup-icon.svg'

import SetupProgress from '../../common/adminTaskStep/SetupProgress'
import SetupProgressSteps from '../../common/adminTaskStep/SetupProgressSteps'

const PerksRenewalTasks = () => {
  const history = useHistory()

  const [tasks, isLoading] = useRenewalTasks()
  const [inviteEmployeesModalVisible, setInviteEmployeesModalVisible] = useState(false)
  const [activeStepIndex, setActiveStepIndex] = useState(0)

  const { data: activePlanPeriod } = useQueryRequestV2<PlanPeriodDto>({
    queryFn: () => getActivePlanPeriod(),
  })

  const { data: renewalPlanPeriod } = useQueryRequestV2<RenewalPlanPeriodResponse>({
    queryFn: () => getRenewalPlanPeriod(),
  })

  const renewalYear = formatYear(renewalPlanPeriod?.startDate)

  const { isLg } = useBreakPoints()

  const [, setPageTitle] = useSharedState<{
    title: string
    showBackButton: boolean
  }>('PageTitle')

  const taskCallbackMapper = new Map<number, () => void>([
    [
      PerksRenewalTaskIds.DesignBenefit,
      () => history.push(`${ROUTE_PATHS.BENEFIT_DETAILS_PATH}?planPeriodId=${renewalPlanPeriod.id}`),
    ],
    [PerksRenewalTaskIds.AssignClasses, () => history.push(ROUTE_PATHS.ASSIGN_CLASSES)],
    [PerksRenewalTaskIds.InviteEmployees, () => setInviteEmployeesModalVisible(true)],
  ])

  useEffect(() => {
    setPageTitle({
      title: `Benefit renewal`,
      showBackButton: true,
    })

    return () => {
      setPageTitle(null)
    }
  }, [renewalPlanPeriod?.id])

  useEffect(() => {
    tasks && setActiveStepIndex(tasks.findIndex((x) => x.status == TaskStatus.Active))
  }, [tasks])

  return (
    <Skeleton loading={isLoading}>
      {renewalPlanPeriod && (
        <InviteYourEmployeesModal
          visible={inviteEmployeesModalVisible}
          title={'Notify your employees'}
          infoMessage={'Peoplekeep will send your employees an email informing them of their new benefit.'}
          checkboxLabel="I have added all of my existing employees."
          benefitStartDate={renewalPlanPeriod.startDate}
          inviteApi={notifyEmployees}
          onCancel={() => setInviteEmployeesModalVisible(false)}
          onSendInviteToEmployees={() => void (window.location.href = ROUTE_PATHS.ADMIN_DASHBOARD_PATH)}
          showAllEmployeesAddedCheckBox={false}
        />
      )}

      {tasks && (
        <>
          <div className="container-100-percent flex fd-col mt-32 inner-padding bg-white" style={{ maxWidth: 700 }}>
            <div className="main-wrapper inner-padding flex flex__column">
              {tasks.length > 0 && (
                <>
                  <div>
                    <div className="align_self_center mt-24 mb-24 text-center">
                      <img src={Icon} alt="dashboard-logo" className={isLg ? '' : 'w-100'} />
                    </div>

                    <h1>Let’s configure your WorkPerks benefits for renewal!</h1>

                    {activePlanPeriod && (
                      <p className="gray-7 mb-16">
                        To renew your benefits for the upcoming plan period, you can reconfigure your WorkPerks benefit
                        design. If you begin making changes, and do not complete the benefit renewal process, the system
                        will auto-renew your benefit using existing settings and your employees will be notified of the
                        new benefit.
                      </p>
                    )}
                  </div>
                  <SetupProgress title={'Benefit Renewal'} steps={tasks} activeStep={activeStepIndex} />
                  <SetupProgressSteps
                    steps={tasks}
                    renewalYear={renewalYear}
                    activeStep={activeStepIndex}
                    onStepClick={(taskId) => {
                      taskCallbackMapper.get(taskId)()
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </>
      )}
    </Skeleton>
  )
}

export default PerksRenewalTasks
